import React, {useState, useEffect} from 'react';
import {useSelector} from 'react-redux';
import {Link} from 'react-router';
import useGet from "../../hooks/useGet";
import BackLink from "../../components/BackLink";
import {classNames} from "../../utils/miscUtils";
import usePreCruisePassenger from "../../hooks/usePreCruisePassenger";
import {postToHub} from "../../actions/networkActions";
import InfoBox from "../../components/passengerInformation/InfoBox";
import {fetchSectionsFromHub} from "../../actions/preCruiseActions";
import { Trans, useLingui } from '@lingui/react/macro';
import { navigate } from '../../utils/navigationUtils';

function PassengerReview(props) {
    const {passengerId, type} = props.params;
    const passenger = usePreCruisePassenger(passengerId);
    const passengerName = `${passenger.firstName} ${passenger.lastName}`;
    const isMedicalMobilityPage = type === "medicalAndMobility";
    const questionCategory = isMedicalMobilityPage ? "prerequisiteTravelInfo" : "travelInfo";
    const questionsUrl = `precruise/questions/${questionCategory}/${passengerId}`;
    const sections = useSelector(state => state.preCruise.sections);
    const bookingType = useSelector(state => state.authentication.bookingSubType) || props.location?.state?.bookingType;
    const [didFetch, setDidFetch] = useState(false);

    const {t} = useLingui();

    useEffect(() => {
        if(didFetch === false) {
            fetchSectionsFromHub(bookingType, questionCategory);
            setDidFetch(true);
        }
    },[bookingType, didFetch, questionCategory]);

    const data = useGet({
        path: questionsUrl,
        logoutOn403: false,
    }, false);

    const resetLock = () =>{
        const finishUrl = t({id: "/precruise/passenger"}) + `/${passengerId}` + t({id: "/lock"}) + `${isMedicalMobilityPage ? "/medicalAndMobility" : ""}`;
        navigate(finishUrl);
    }

    if(!data) {
        return (
            <div className="container mx-auto mt-6">
                <span translate="no"><Trans id="loading">loading</Trans>...</span>
            </div>
        )
    }

    if(data.error) {
        if(data.error.response?.status === 403) {
            navigate( t({id: "/precruise/passenger"}) + `/${passengerId}` + t({id: "/unlock"}) +`${isMedicalMobilityPage ? "/medicalAndMobility" : ""}`);
        }
        else {
            return (
                <div>
                    <Trans id="unexpected_error_try_again">An unexpected error has happened please try again.</Trans>
                </div>
            )
        }
    }

    return (
        <div>
            <div className="container mx-auto">
                <div className="mt-6">
                    <BackLink to={t({id: "/dashboard"})} text={t({id: "back_to_precruise_information_dashboard"})}/>
                </div>



                <h1>
                    <Trans id="precruise_information_for">Pre-cruise information for</Trans> {passengerName}
                </h1>
            </div>

            <div className="bg-primary-lightest shadow mb-12 mt-6">
                <div className="container mx-auto py-6">
                    {passenger.isTravelInfoSecured &&
                        <LockInfo passengerId={passengerId} isMedicalMobilityPage={isMedicalMobilityPage}/>
                    }

                    {sections.map((section, index) =>
                        <Section key={section.hubId}
                                 section={section}
                                 index={index}
                                 passengerId={passengerId}
                                 sectionData={data[section.hubId]}
                                 isMedicalMobilityPage={isMedicalMobilityPage}
                        />
                    )}

                    {passenger.secureReset && (
                        <div className="mt-6">
                            <button className={`action-button w-mobile action-button-enabled`} onClick={resetLock}>
                                <Trans id="update_your_lock_status">Update your Lock Status</Trans>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default PassengerReview;

// TODO - get feedback on how this should look like and if frontend should do a redirect on x mins of inactivity just like we do for agency logout
function LockInfo(props) {
    const [lockError, setLockError] = useState("");
    const {t} = useLingui();

    const lockData = async () => {
        const result = await postToHub('precruise/passenger/closeSession', {passengerId: props.passengerId});

        if(result === "") {
            navigate(t({id: "/precruise/passengers"}) + `${props.isMedicalMobilityPage ? "/medicalAndMobility" : ""}`);
        }
        else {
            setLockError(t({id: "error_when_securing_data_text"}));
        }
    }

    return (
        <InfoBox classes="mb-6"
            infoText={t({id: "protected_data_view"})}
            description={t({id: "protected_data_view_description"})}
        >
            <div className="mt-4">
                <button className="action-button action-button-enabled" onClick={lockData}>
                    <Trans id="lock_information">Lock Information</Trans>
                </button>
            </div>
            {lockError &&
            <div className="mt-4 text-red">
                {lockError}
            </div>}
        </InfoBox>
    )
}

function Section(props) {
    const section = props.section;
    const answers = props.sectionData?.answers || {};
    const {t} = useLingui();


    const questions = sortQuestions(flattenQuestions(props.sectionData.questions)).filter(question => answers[question.id]);
    const editUrl = t({id: "/precruise/passenger"}) + `/${props.passengerId}` + t({id: "/form"}) +`${props.isMedicalMobilityPage ? "/medicalAndMobility" : ""}`;

    return (
        <div className={classNames("relative bg-white p-4 shadow", props.index !== 0 && "mt-6")} key={section.hubId}>
            <h2 className="mr-8">
                {section.reviewLabel}
            </h2>
            <Link
                className="absolute top-0 right-0 mt-6 mr-4 text-primary underline"
                to={{pathname: editUrl}}
                state={{section: props.index}}
            >
                <Trans id="edit">Edit</Trans>
            </Link>
            <div>
                {/* Can this be removed as cardInfo doesn't have review page */}
                {/* I THINK yes, but further checks should be done with the hub boys to confirm when this value is coming and is not coming back */}
                {props.sectionData.cardInfo?.cardNumber &&
                    <CCInfo cardInfo={props.sectionData.cardInfo}/>
                }
                {!props.sectionData.cardInfo?.cardNumber &&
                    <QuestionsAndAnswers questions={questions} answers={answers}/>
                }
            </div>
        </div>
    );
}

function QuestionsAndAnswers(props) {
    const propQuestions = props.questions || [];
    const answers = props.answers;

    const nrQuestions = propQuestions.length;
    const showInColumns = nrQuestions > 6;

    let displayQuestions = [propQuestions];
    if(showInColumns) {
        displayQuestions = [propQuestions.slice(0, nrQuestions / 2), propQuestions.slice(nrQuestions / 2)];
    }


    return (
        <div className="xl:flex gap-4">
            {
                displayQuestions.map((questions, index) => <div key={index} className={`${showInColumns ? "xl:w-1/2" : "w-full"}`}>
                    {questions.map(question => <div className="mt-4 text-lg" key={question.id}>
                        {question.questionSectionReviewLabel && <div className='text-primary-dark mb-2 uppercase'> {question.questionSectionReviewLabel} </div> }
                        <div className="text-grey-darker">
                            {question.text}
                        </div>
                        <div>
                            {
                                Array.isArray(answers[question.id]) ?
                                    answers[question.id].join(", ") :
                                    answers[question.id]
                            }
                        </div>
                    </div>
                    )}
                </div>)
            }
        </div>
    )

}

function CCInfo(props) {
    const cardInfo = props.cardInfo;

    if(!cardInfo?.cardNumber) {
        return null;
    }

    return(
        <div>
            <div className="mt-4 text-lg">
                <div className="text-grey-darker">
                    Card number
                </div>
                <div>
                    **** **** **** {cardInfo.cardNumber.substr(-4)}
                </div>
            </div>

            <div className="mt-4 text-lg">
                <div className="text-grey-darker">
                    Card details applied to
                </div>
                <div>
                    {cardInfo.cardAppliesTo.join(", ")}
                </div>
            </div>
        </div>
    );
}

function flattenQuestions(questions, forceToEnd = false) {
    const result = [];

    questions.forEach(question => {
        const moveToEnd = forceToEnd || question.moveToEndOfPage;
        // add the question to the result
        result.push({
            id: question.id,
            text: question.reviewText || question.questionText,
            questionSectionReviewLabel: question.questionSectionReviewLabel,
            moveToEnd,
        });

        // add the nested questions to the result
        Object.keys(question.questions || []).forEach(key => {
            result.push(...flattenQuestions(question.questions[key], moveToEnd));
        });
    })

    return result;
}

// bring all questions that have moveToEnd set as true to the end
function sortQuestions(questions) {
    return [...questions.filter(question => !question.moveToEnd), ...questions.filter(question => question.moveToEnd)];
}
